import { Mail } from 'lucide-react';
import { Message } from 'iconsax-react';
import type { ColumnDef } from '@tanstack/react-table';

import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import { Text } from '@src/design-system/text';
import { REMINDER_CHANNELS, type TReminder } from '@src/shared/types/reminder';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { WhatsappIcon } from '@shared/assets/icons/shared/whatsapp-icon';

function formatScheduledDate(isoDate: string) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function normalizeChannel(ch: string) {
  return ch.trim().toUpperCase();
}

function ChannelCell({ channel }: { channel: string }) {
  const c = normalizeChannel(channel);
  if (c === REMINDER_CHANNELS.whatsapp || c === 'WHATSAPP') {
    return (
      <span className='inline-flex items-center gap-2 text-sm font-medium text-gray-1000'>
        <span className='inline-flex size-5 shrink-0 items-center justify-center bg-[#D1FAE5] [&_svg]:h-5 [&_svg]:w-5'>
          <WhatsappIcon />
        </span>
        {formatTextForDisplay(channel)}
      </span>
    );
  }
  if (c === REMINDER_CHANNELS.sms || c === 'SMS') {
    return (
      <span className='inline-flex items-center gap-2 text-sm font-medium text-gray-1000'>
        <span className='w-6 h-6 bg-[#DBEAFE] rounded-[4px] flex items-center justify-center'>
          <Message
            size={12}
            className='text-blue-500 shrink-0'
          />
        </span>
        {formatTextForDisplay(channel)}
      </span>
    );
  }
  return (
    <span className='inline-flex items-center gap-2 text-sm font-medium text-gray-1000'>
      <span className='w-6 h-6 bg-[#EEE9FF] rounded-[4px] flex items-center justify-center'>
        <Mail
          size={12}
          className='text-[#6C47FF] shrink-0'
        />
      </span>
      {formatTextForDisplay(channel)}
    </span>
  );
}

export function getAudienceLabel(reminder: TReminder): string {
  if (reminder.program) {
    return reminder.program.name + ' - ' + 'program';
  } else return 'Leads';
}

export const upcomingRemindersColumns: ColumnDef<TReminder>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Reminder Name',
    cell: ({ row }) => (
      <span className='line-clamp-2 text-sm font-medium text-gray-800 max-w-[220px]'>
        {row.original.name}
      </span>
    ),
  },
  {
    id: 'createdBy',
    accessorFn: (row) =>
      `${row.created_by?.first_name ?? ''} ${row.created_by?.last_name ?? ''}`.trim(),
    header: 'Created By',
    cell: ({ row }) => {
      const u = row.original.created_by;
      const name = `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim() || '—';
      const initials = name
        .split(' ')
        .filter(Boolean)
        .map((p) => p[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
      return (
        <div className='flex items-center gap-2 min-w-0'>
          <Avatar className='size-8 shrink-0'>
            {u?.avatar ? (
              <AvatarImage
                src={u.avatar}
                alt=''
              />
            ) : null}
            <AvatarFallback className='text-xs'>
              {initials || '?'}
            </AvatarFallback>
          </Avatar>
          <Text className='text-sm font-medium text-gray-800 truncate'>
            {name}
          </Text>
        </div>
      );
    },
  },
  {
    id: 'channel',
    accessorKey: 'notification_channel',
    header: 'Channel',
    cell: ({ row }) => (
      <ChannelCell channel={row.original.notification_channel} />
    ),
  },
  {
    id: 'audience',
    accessorFn: (row) => getAudienceLabel(row),
    header: 'Audience',
    enableSorting: false,
    cell: ({ row }) => (
      <span className='line-clamp-2 text-sm text-gray-800'>
        {getAudienceLabel(row.original)}
      </span>
    ),
  },
  {
    id: 'filterList',
    accessorKey: 'view.name',
    header: 'Filter / List view',
    enableSorting: false,
    cell: ({ row }) => (
      <span className='line-clamp-2 text-sm text-gray-800 max-w-[200px]'>
        {row.original.view?.name || '—'}
      </span>
    ),
  },
  {
    id: 'scheduled',
    accessorKey: 'date',
    header: 'Scheduled',
    cell: ({ row }) => (
      <span className='text-sm text-gray-800 whitespace-nowrap'>
        {formatScheduledDate(row.original.date)}
      </span>
    ),
  },
];
